import { dashboardModuleDocs } from "./dashboardModuleDocs";
import { learningModuleDocs } from "./learningModuleDocs";
import { assignmentModuleDocs } from "./assignmentModuleDocs";
import { certificationModuleDocs } from "./certificationModuleDocs";
import { learningPathModuleDocs } from "./learningPathModuleDocs";
import { administrationModuleDocs } from "./administrationModuleDocs";
import { analyticsModuleDocs } from "./analyticsModuleDocs";
import { engagementModuleDocs } from "./engagementModuleDocs";
import { settingsModuleDocs } from "./settingsModuleDocs";
import { enterpriseModuleDocs } from "./enterpriseModuleDocs";
import { authModuleDocs } from "./authModuleDocs";
import { templateModuleDocs } from "./templateModuleDocs";
import { miscellaneousModuleDocs } from "./miscellaneousModuleDocs";
import {
  ADMIN_DASHBOARD,
  ADMIN_COURSE_CATALOG,
  ADMIN_COURSE_ADD,
  ADMIN_COURSE_ASSIGNMENT,
  ADMIN_COURSE_CATEGORIES,
  ADMIN_REPORTS_TRENDING,
  ADMIN_CERTIFICATIONS,
  ADMIN_CERTIFICATION_ASSIGNMENT,
  ADMIN_LEARNING_PATHS,
  ADMIN_LEARNING_PATH_CREATE,
  ADMIN_LP_WORKFLOW_LIST,
  ADMIN_LEARNING_PATH_ASSIGNMENT,
  ADMIN_USERS,
  ADMIN_USERS_DEPARTMENTS,
  ADMIN_ROLES,
  ADMIN_USERS_INVITATIONS,
  ADMIN_ANALYTICS,
  ADMIN_ANALYTICS_COURSE_PERFORMANCE,
  ADMIN_ANALYTICS_CERTIFICATION_PERFORMANCE,
  ADMIN_ANALYTICS_LEARNING_PATH_PERFORMANCE,
  ADMIN_ANALYTICS_EMPLOYEE_PROGRESS,
  ADMIN_ANALYTICS_MY_TEAM_PROGRESS,
  ADMIN_ANALYTICS_DEPARTMENT_PROGRESS,
  ADMIN_ANALYTICS_ROLE_PROGRESS,
  ADMIN_REPORTS_ACTIVITY,
  ADMIN_REPORTS_AUDIT_TRAIL,
  ADMIN_REPORTS_TENANT,
  ADMIN_REPORTS_AI_COST,
  ADMIN_AI_USAGE,
  ADMIN_EMAIL_LOGS,
  ADMIN_TRAINING_STATUS_REPORT,
  ADMIN_NOTIFICATIONS,
  ADMIN_PROFILE,
  ADMIN_GAMIFICATION_BADGES,
  ADMIN_GAMIFICATION_USER_ACHIEVEMENTS,
  ADMIN_LEADERBOARD,
  ADMIN_REVIEWS,
  ADMIN_KNOWLEDGE_DOCUMENTS,
  ADMIN_SETTINGS_BUSINESS,
  ADMIN_SETTINGS_TICKET_REQUESTS,
  ADMIN_SSO_CONFIGURATION,
  ADMIN_SCIM_CONFIGURATION,
  ADMIN_CREDIT_PLANS,
  ADMIN_EMAIL_TEMPLATES,
  ADMIN_CERTIFICATE_TEMPLATES,
  ADMIN_SETTINGS_WEBHOOKS,
  AUTH_LOGIN,
  AUTH_REGISTER,
  AUTH_FORGOT_PASSWORD,
  ERROR_UNAUTHORIZED,
  ERROR_ACCESS_DENIED,
} from "@/constants/constant-routes";

export interface ModuleDocContent {
  title: string;
  subtitle: string;
  color: string;
  badge: string;
  description: string;
  content: string;
}

export interface ModuleCategory {
  name: string;
  icon: string;
  color: string;
  description: string;
  modules: string[];
}

export const moduleCategories: ModuleCategory[] = [
  {
    name: "Dashboard",
    icon: "layout-dashboard",
    color: "blue",
    description: "Platform overview, metrics, and quick actions",
    modules: ["admin-dashboard"],
  },
  {
    name: "Courses",
    icon: "book-open",
    color: "blue",
    description: "Course catalog, AI generation, categories, and trending topics",
    modules: ["course-catalog", "ai-course-gen", "categories", "trending-topics"],
  },
  {
    name: "Course Assignment",
    icon: "clipboard-check",
    color: "blue",
    description: "Assign courses to learners and track progress",
    modules: ["course-assignment"],
  },
  {
    name: "Certifications",
    icon: "award",
    color: "violet",
    description: "AI-powered certification builder",
    modules: ["certification-builder"],
  },
  {
    name: "Certification Assignment",
    icon: "file-check",
    color: "violet",
    description: "Assign certifications and track completion",
    modules: ["certification-assignment"],
  },
  {
    name: "Learning Paths",
    icon: "map",
    color: "indigo",
    description: "Structured learning journeys, AI path generation, and workflows",
    modules: ["path-builder", "ai-path-gen", "path-workflow"],
  },
  {
    name: "Learning Path Assignment",
    icon: "route",
    color: "indigo",
    description: "Assign learning paths and monitor journey progress",
    modules: ["learning-path-assignment"],
  },
  {
    name: "User Management",
    icon: "shield",
    color: "amber",
    description: "Users, groups, roles, permissions, and invitations",
    modules: ["user-management", "invitations", "group-management", "role-permission-mgmt"],
  },
  {
    name: "Reports & Analytics",
    icon: "bar-chart-3",
    color: "emerald",
    description: "Performance analytics, progress tracking, activity logs, and reports",
    modules: [
      "analytics-overview",
      "employee-progress",
      "my-team-progress",
      "department-progress",
      "role-progress",
      "course-analytics",
      "lp-analytics",
      "cert-analytics",
      "activity-report",
      "ai-usage",
      "training-status-report",
      "email-logs",
      "audit-trail-report",
      "tenant-reports",
      "ai-cost-tracking",
    ],
  },
  {
    name: "Gamification",
    icon: "trophy",
    color: "rose",
    description: "Badges, achievements, and leaderboards",
    modules: ["leaderboard", "gamification", "user-achievements"],
  },
  {
    name: "Reviews",
    icon: "star",
    color: "rose",
    description: "Course reviews and ratings",
    modules: ["reviews"],
  },
  {
    name: "Knowledge Store",
    icon: "library",
    color: "slate",
    description: "Document stores, file uploads, and AI-powered search",
    modules: ["knowledge-base"],
  },
  {
    name: "Settings",
    icon: "settings",
    color: "slate",
    description: "Platform configuration, business settings, and integrations",
    modules: ["business-settings", "ticket-requests", "webhooks"],
  },
  {
    name: "Templates",
    icon: "file-text",
    color: "purple",
    description: "Email and certificate template management",
    modules: ["email-templates", "certificate-templates"],
  },
  {
    name: "Enterprise",
    icon: "building-2",
    color: "slate",
    description: "SSO, SCIM, and credit plans configuration",
    modules: ["credit-plans", "sso", "scim"],
  },
  {
    name: "Miscellaneous",
    icon: "bell",
    color: "blue",
    description: "Cross-cutting features — in-app and email notifications, scheduling, history, and preferences",
    modules: ["notifications", "profile"],
  },
  {
    name: "Authentication & Error Pages",
    icon: "log-in",
    color: "red",
    description: "Login, registration, password recovery, and error pages",
    modules: [
      "auth-login",
      "auth-register",
      "auth-forgot-password",
      "error-401",
      "error-403",
      "error-404",
    ],
  },
];

// Maps each doc module key to its admin panel route (using constant-routes)
export const moduleRoutes: Record<string, string> = {
  // Dashboard
  "admin-dashboard": ADMIN_DASHBOARD,
  // Courses
  "course-catalog": ADMIN_COURSE_CATALOG,
  "ai-course-gen": ADMIN_COURSE_ADD,
  "course-assignment": ADMIN_COURSE_ASSIGNMENT,
  "categories": ADMIN_COURSE_CATEGORIES,
  "trending-topics": ADMIN_REPORTS_TRENDING,
  // Certifications
  "certification-builder": ADMIN_CERTIFICATIONS,
  "certification-assignment": ADMIN_CERTIFICATION_ASSIGNMENT,
  // Learning Paths
  "path-builder": ADMIN_LEARNING_PATHS,
  "ai-path-gen": ADMIN_LEARNING_PATH_CREATE,
  "path-workflow": ADMIN_LP_WORKFLOW_LIST,
  "learning-path-assignment": ADMIN_LEARNING_PATH_ASSIGNMENT,
  // User Management
  "user-management": ADMIN_USERS,
  "group-management": ADMIN_USERS_DEPARTMENTS,
  "role-permission-mgmt": ADMIN_ROLES,
  "invitations": ADMIN_USERS_INVITATIONS,
  // Reports & Analytics
  "analytics-overview": ADMIN_ANALYTICS,
  "course-analytics": ADMIN_ANALYTICS_COURSE_PERFORMANCE,
  "cert-analytics": ADMIN_ANALYTICS_CERTIFICATION_PERFORMANCE,
  "lp-analytics": ADMIN_ANALYTICS_LEARNING_PATH_PERFORMANCE,
  "employee-progress": ADMIN_ANALYTICS_EMPLOYEE_PROGRESS,
  "my-team-progress": ADMIN_ANALYTICS_MY_TEAM_PROGRESS,
  "department-progress": ADMIN_ANALYTICS_DEPARTMENT_PROGRESS,
  "role-progress": ADMIN_ANALYTICS_ROLE_PROGRESS,
  "activity-report": ADMIN_REPORTS_ACTIVITY,
  "audit-trail-report": ADMIN_REPORTS_AUDIT_TRAIL,
  "tenant-reports": ADMIN_REPORTS_TENANT,
  "ai-cost-tracking": ADMIN_REPORTS_AI_COST,
  "ai-usage": ADMIN_AI_USAGE,
  "email-logs": ADMIN_EMAIL_LOGS,
  "training-status-report": ADMIN_TRAINING_STATUS_REPORT,
  // Miscellaneous
  "notifications": ADMIN_NOTIFICATIONS,
  "profile": ADMIN_PROFILE,
  // Gamification
  "gamification": ADMIN_GAMIFICATION_BADGES,
  "user-achievements": ADMIN_GAMIFICATION_USER_ACHIEVEMENTS,
  "leaderboard": ADMIN_LEADERBOARD,
  // Reviews
  "reviews": ADMIN_REVIEWS,
  // Knowledge Store
  "knowledge-base": ADMIN_KNOWLEDGE_DOCUMENTS,
  // Settings
  "business-settings": ADMIN_SETTINGS_BUSINESS,
  "ticket-requests": ADMIN_SETTINGS_TICKET_REQUESTS,
  "webhooks": ADMIN_SETTINGS_WEBHOOKS,
  // Templates
  "email-templates": ADMIN_EMAIL_TEMPLATES,
  "certificate-templates": ADMIN_CERTIFICATE_TEMPLATES,
  // Enterprise
  "sso": ADMIN_SSO_CONFIGURATION,
  "scim": ADMIN_SCIM_CONFIGURATION,
  "credit-plans": ADMIN_CREDIT_PLANS,
  // Authentication & Error Pages
  "auth-login": AUTH_LOGIN,
  "auth-register": AUTH_REGISTER,
  "auth-forgot-password": AUTH_FORGOT_PASSWORD,
  "error-401": ERROR_UNAUTHORIZED,
  "error-403": ERROR_ACCESS_DENIED,
  "error-404": "/not-found",
};

export const moduleDocsData: Record<string, ModuleDocContent> = {
  ...dashboardModuleDocs,
  ...learningModuleDocs,
  ...assignmentModuleDocs,
  ...certificationModuleDocs,
  ...learningPathModuleDocs,
  ...administrationModuleDocs,
  ...analyticsModuleDocs,
  ...engagementModuleDocs,
  ...settingsModuleDocs,
  ...enterpriseModuleDocs,
  ...authModuleDocs,
   ...templateModuleDocs,
   ...miscellaneousModuleDocs,
};
